import { Box, Paper, TextField, Typography } from '@mui/material'
import React, { Fragment, useEffect, useState, useMemo } from 'react'
import { Batch } from '../../../interfaces/Batch'
import { BatchTable } from '../../../components/_WMS/ListBatch/BatchTable'
import { SkuFefoType } from '../../../components/_WMS/ListBatch/SkuFefoType'

interface SelectedBatchInfo {
  rowId: string // Nuevo: identificador único de fila
  orderDetailId: number
  batchId: number
  batchNumber: string
  expirationDate?: string
}

interface SelectedSkuInfo {
  skuId: number
  sku: string
  orderDetailId: number
  rowId?: string // Nuevo campo
  hasBatch?: boolean
  hasExpirationDate?: boolean
}

interface SimpleBatchSelectorProps {
  handleCloseModal: () => void
  batchList: Batch[]
  positionCode?: string
  selectedSku: SelectedSkuInfo
  onBatchSelected: (selectedBatch: SelectedBatchInfo) => void
}

export const SimpleBatchSelector: React.FC<SimpleBatchSelectorProps> = ({
  batchList,
  selectedSku,
  handleCloseModal,
  positionCode,
  onBatchSelected
}) => {
  const [searchTerm, setSearchTerm] = useState('')

  // Filtrar lotes basado en el término de búsqueda
  const filteredBatchList = useMemo(() => {
    if (!searchTerm.trim()) {
      return batchList || []
    }

    return (batchList || []).filter((batch) =>
      batch.batchNumber.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [batchList, searchTerm])

  const buildFefoType = (): SkuFefoType => {
    const batchNumber = selectedSku?.hasBatch || false
    const expirationDate = selectedSku?.hasExpirationDate || false

    return {
      hasBatchNumber: batchNumber,
      hasExpirationDate: expirationDate,
      modalTitle:
        batchNumber && expirationDate
          ? 'Lotes y Vencimientos'
          : batchNumber
          ? 'Lotes'
          : 'Fechas de Vencimiento',
      modalType:
        batchNumber && expirationDate
          ? 'both'
          : batchNumber
          ? 'batch'
          : 'expiration'
    }
  }

  const [skuFefoType, setSkuFefoType] = useState<SkuFefoType>(() =>
    buildFefoType()
  )

  // Cuando cambie selectedSku recalcular:
  useEffect(() => {
    setSkuFefoType(buildFefoType())
  }, [selectedSku])

  const handleSelectBatch = (batchId: number) => {
    const batch = batchList?.find((it) => it.batchId === batchId)

    if (batch) {
      const selectedBatchInfo: SelectedBatchInfo = {
        rowId: selectedSku.rowId || `${selectedSku.orderDetailId}_0`, // Usa el rowId
        orderDetailId: selectedSku.orderDetailId,
        batchId: batch.batchId,
        batchNumber: batch.batchNumber,
        expirationDate: batch?.expirationDate || undefined
      }

      onBatchSelected(selectedBatchInfo)
    }

    handleCloseModal()
  }

  const handleClearSearch = () => {
    setSearchTerm('')
  }

  return (
    <Fragment>
      {positionCode && (
        <Typography>{`Lotes en Posición ${positionCode}`}</Typography>
      )}
      <Typography
        variant='body2'
        color='textSecondary'
        style={{ marginBottom: '16px' }}
      >
        SKU: {selectedSku.sku}
      </Typography>

      {/* Campo de búsqueda */}
      <Box sx={{ margin: '14px 0' }}>
        <Paper style={{ padding: '10px' }}>
          <TextField
            fullWidth
            variant='outlined'
            placeholder='Buscar lote por número...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
          />
          {searchTerm && (
            <Box sx={{ textAlign: 'right', marginTop: '8px' }}>
              <Typography
                variant='body2'
                style={{ cursor: 'pointer', color: '#1976d2' }}
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Mostrar resultados */}
      <BatchTable
        batches={filteredBatchList}
        showBatchNumber={skuFefoType.hasBatchNumber}
        showExpirationDate={skuFefoType.hasExpirationDate}
        modalType={skuFefoType.modalType}
        onBatchClick={(batch) => handleSelectBatch(batch.batchId)}
        emptyText={
          searchTerm
            ? `No se encontraron lotes que contengan "${searchTerm}"`
            : 'No hay lotes disponibles'
        }
      />

      {/* Mostrar contador de resultados */}
      {searchTerm && (
        <Box sx={{ textAlign: 'center', marginTop: '8px' }}>
          <Typography variant='body2' color='textSecondary'>
            {filteredBatchList.length} de {batchList?.length || 0} lotes
            encontrados
          </Typography>
        </Box>
      )}
    </Fragment>
  )
}
